import { isAxiosError } from 'axios';
import { z } from 'zod';

import type {
  KnownDeliveryStatus,
  PENDING_DELIVERY_STATUS,
} from '@/features/chat/delivery-status';
import { apiClient } from '@/lib/api-client';

/**
 * Type-safe wrappers around the Contract C chat surface
 * (`hub/app/api/conversations.py::list_messages` +
 *  `hub/app/api/messages.py::send_message`).
 *
 * Backend ordering for the history endpoint is `created_at DESC` — newest
 * first — paged via `limit`/`offset`. The UI presents oldest-at-top /
 * newest-at-bottom; the page-flatten step in `useMessages` is the only
 * place the ordering is flipped.
 *
 * `POST .../messages` is `multipart/form-data` with three fields: `text`,
 * `file`, and `client_ref` (the console-supplied opaque echo, ≤128 chars).
 * The endpoint returns the persisted `MessageOut`; `client_ref` round-trips
 * unchanged and is the cache-patch key for the optimistic bubble.
 */

// --- Schemas (mirror hub/app/api/schemas.py) -------------------------------

export const mediaOutSchema = z.object({
  mime: z.string().nullable(),
  size_bytes: z.number().int().nonnegative().nullable(),
  filename: z.string().nullable(),
  url: z.string(),
});

export type MessageMedia = z.infer<typeof mediaOutSchema>;

/** One reactor's current emoji reaction on a message — mirror of the
 *  backend's `ReactionOut` (`hub/app/api/schemas.py`). WhatsApp semantics:
 *  one reaction per reactor per message; the backend deletes on removal, so
 *  `emoji` is never empty here.
 *
 *  `reactor_kind` is the backend CHECK's literal set — 'customer' | 'phone'
 *  | 'agent' — kept as a plain string so a future kind doesn't break the
 *  parse. `reactor_agent_id` is set iff kind === 'agent'; the console uses
 *  it to mark the current agent's OWN reaction (the one a repeat click
 *  removes). */
export const reactionOutSchema = z.object({
  emoji: z.string(),
  reactor_kind: z.string(),
  reactor_agent_id: z.string().uuid().nullable(),
});

export type Reaction = z.infer<typeof reactionOutSchema>;

/** Delivery status as carried by `MessageOut.delivery_status`: the Contract A
 *  vocabulary (`DELIVERY_STATUSES` in `delivery-status.ts` — received /
 *  queued / sent / delivered / read / failed / unknown), the client-only
 *  `'pending'` of an optimistic placeholder, or — forward compat — any other
 *  string. The zod schema keeps the field a plain `z.string()` so a future
 *  server-side addition never breaks the parse; the UI must render an
 *  unrecognised value neutrally (a question mark, never a "sent" check —
 *  spec 02 §3). */
export type DeliveryStatus =
  | KnownDeliveryStatus
  | typeof PENDING_DELIVERY_STATUS
  | string;

export const messageOutSchema = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  /** The OUR-SIDE account (shop number) this message travelled through —
   *  `messages.account_id`, additive on `MessageOut` since the merged
   *  phone-thread work; present on every REST response AND the WS
   *  `message.new` payload. `.optional()` (not required) on purpose:
   *  locally-built optimistic placeholders and pre-feature cache entries
   *  may omit it — consumers that need it fall back to resolving via
   *  `conversation_id` against the conversations cache. */
  account_id: z.string().uuid().optional(),
  direction: z.enum(['inbound', 'outbound']),
  content_type: z.string(), // 'text' | 'image' | 'document' | 'audio' | ...
  content_text: z.string().nullable(),
  media: mediaOutSchema.nullable(),
  location: z.record(z.unknown()).nullable(),
  /** This message's own WhatsApp wamid. `null` until the platform assigns
   *  one (e.g. a brand-new optimistic outbound row) or for message types
   *  the adapter doesn't tag. This is the value another message's
   *  `reply_to_platform_message_id` points at when quoting this one. */
  platform_message_id: z.string().nullable(),
  reply_to_platform_message_id: z.string().nullable(),
  sender_kind: z.string(), // 'customer' | 'agent' | 'system'
  sender_agent_id: z.string().uuid().nullable(),
  platform_ts: z.string().nullable(),
  created_at: z.string(),
  delivery_status: z.string(),
  /** 2026-09 honest send status (spec 00 §3.2 `MessageOut` delta; hub
   *  migration 0015):
   *    - `delivery_error` — why the row is `failed` / `unknown` (the hub's
   *      text, e.g. "send result unknown (no gateway ack)"); `null`
   *      otherwise — a `delivered` / `read` receipt clears it.
   *    - `delivered_at` / `read_at` — when the customer's device acked /
   *      opened the message (receipts; set once, never cleared).
   *  `.nullable().optional()` (same reasoning as `resend_of_message_id`):
   *  locally-built optimistic placeholders and pre-0015 hubs omit them. */
  delivery_error: z.string().nullable().optional(),
  delivered_at: z.string().nullable().optional(),
  read_at: z.string().nullable().optional(),
  client_ref: z.string().nullable(),
  /** Current reactions on this message. `.optional()` (not `.default([])`)
   *  on purpose: locally-built cache entries (optimistic placeholders,
   *  pre-feature fixtures) may omit it, and consumers read
   *  `message.reactions ?? []` — so absent and empty are equivalent and no
   *  constructor is forced to carry the field. */
  reactions: z.array(reactionOutSchema).optional(),
  /** Set on a row created by `POST /messages/{id}/resend`: the SOURCE
   *  message's id (provenance link — the resent row is a FRESH row, not a
   *  status flip on the source). `null` on ordinary sends. `.nullable()
   *  .optional()` so pre-feature cache entries and locally-built
   *  placeholders (which omit the field entirely) keep parsing. */
  resend_of_message_id: z.string().uuid().nullable().optional(),
});

export type Message = z.infer<typeof messageOutSchema>;

export const pagedMessagesSchema = z.object({
  items: z.array(messageOutSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
});

export type PagedMessages = z.infer<typeof pagedMessagesSchema>;

// --- Request types ---------------------------------------------------------

export interface ListMessagesParams {
  conversationId: string;
  limit?: number;
  offset?: number;
}

export interface ListThreadMessagesParams {
  /** RAW thread grouping key — `COALESCE(phone_e164, peer_id)`, i.e. the
   *  same value `group=phone` groups by (`groupKeyOf`). Starts with `+` for
   *  phone keys, contains `@` for NULL-phone group JIDs — URL-encoded HERE
   *  (`encodeURIComponent`), never by the caller. */
  threadKey: string;
  limit?: number;
  offset?: number;
}

export interface SendMessageParams {
  conversationId: string;
  text?: string | null;
  file?: File | null;
  /** Console-generated UUID; round-trips on `MessageOut.client_ref`. The
   *  cache patch in `useSendMessage` uses this as the optimistic key. */
  clientRef: string;
  /** The quoted message's `platform_message_id` (its wamid) — NOT the
   *  internal `id` UUID. Sent as the `reply_to` multipart field. Omitted
   *  from the request entirely when null/undefined (plain send). */
  replyTo?: string | null;
}

export interface ReactToMessageParams {
  /** The target message's internal `id` (NOT its wamid — the backend
   *  resolves the wamid itself and 409s when the message has none yet). */
  messageId: string;
  /** The emoji to set. WhatsApp replace semantics server-side: it REPLACES
   *  the calling agent's previous reaction on the message, and the EMPTY
   *  STRING REMOVES it. */
  emoji: string;
}

// --- API surface -----------------------------------------------------------

/**
 * `GET /api/v1/conversations/{id}/messages` — one page of message history.
 *
 * Backend orders `created_at DESC`; the items inside one page are newest-
 * first. The infinite-query hook flattens pages in order and the
 * MessageList component reverses the result so the viewport reads
 * oldest-at-top, newest-at-bottom (the standard chat UX).
 */
export async function listMessages(
  params: ListMessagesParams,
): Promise<PagedMessages> {
  const { conversationId, limit = 50, offset = 0 } = params;
  const response = await apiClient.get(
    `/conversations/${conversationId}/messages`,
    { params: { limit, offset } },
  );
  return pagedMessagesSchema.parse(response.data);
}

/**
 * `GET /api/v1/threads/{thread_key}/messages` — one page of the MERGED
 * phone-thread history: the union of messages across every conversation
 * (across our accounts) whose contact matches the thread key.
 *
 * Backend orders `created_at DESC, id DESC` — same newest-first paging
 * conventions as `listMessages`, but with the house default limit of 30
 * (this endpoint's own `Query` default). An unknown/unmatched key returns
 * an EMPTY page (`items: [], total: 0`), never a 404 — keys are
 * client-derived. Merge is a READ-TIME view only: sends still target one
 * member conversation via `sendMessage`.
 */
export async function listThreadMessages(
  params: ListThreadMessagesParams,
): Promise<PagedMessages> {
  const { threadKey, limit = 30, offset = 0 } = params;
  const response = await apiClient.get(
    `/threads/${encodeURIComponent(threadKey)}/messages`,
    { params: { limit, offset } },
  );
  return pagedMessagesSchema.parse(response.data);
}

/**
 * `POST /api/v1/conversations/{id}/messages` — outbound send.
 *
 * Body is `multipart/form-data`. axios autodetects this and sets the
 * Content-Type (including the boundary) for us when given a `FormData`;
 * any explicit `Content-Type: application/json` from the default headers
 * is dropped for the per-request override. The server returns the
 * persisted `MessageOut` with the same `client_ref` echoed back.
 */
export async function sendMessage(params: SendMessageParams): Promise<Message> {
  const { conversationId, text, file, clientRef, replyTo } = params;

  const body = new FormData();
  if (text !== undefined && text !== null && text !== '') {
    body.append('text', text);
  }
  if (file) {
    body.append('file', file);
  }
  body.append('client_ref', clientRef);
  if (replyTo !== undefined && replyTo !== null) {
    body.append('reply_to', replyTo);
  }

  const response = await apiClient.post(
    `/conversations/${conversationId}/messages`,
    body,
    {
      // Let axios pick the multipart boundary; explicit `undefined` overrides
      // the json default the shared client sets at create time.
      headers: { 'Content-Type': undefined },
    },
  );
  return messageOutSchema.parse(response.data);
}

/**
 * The EXACT `409` detail the hub answers when an `unknown` source is resent
 * without `confirm_duplicate_risk: true` (spec 00 §3.2 resend row, 02 §4.5).
 * Exported so the resend hook can tell this 409 (→ ask the operator to
 * confirm) apart from the other 409 causes without re-typing the text.
 */
export const RESEND_CONFIRM_REQUIRED_DETAIL =
  'delivery of the source message is unknown; resending may duplicate it — confirm to proceed';

export interface ResendMessageOptions {
  /** Sent as `confirm_duplicate_risk: true` — the operator confirmed that
   *  resending an `unknown` source (the customer may already have it) is
   *  intended. Omitted from the body unless true: the hub defaults it to
   *  `false`, and a `failed` source never needs it. */
  confirmDuplicateRisk?: boolean;
}

/**
 * `POST /api/v1/messages/{id}/resend` — one-click resend of a FAILED (or,
 * once the hub's 2026-09 rule lands, `unknown`) outbound message,
 * optionally through ANOTHER conversation of the same customer
 * (cross-account resend from a merged thread).
 *
 * JSON body: `{"target_conversation_id": "<uuid>"}` to resend into a
 * different member conversation; `{}` for a same-conversation resend;
 * `confirm_duplicate_risk: true` added when {@link ResendMessageOptions}
 * says so. The server rebuilds media from its content-addressed store — the
 * client never re-uploads bytes — and returns 201 with the MessageOut of a
 * FRESH row (new id, `resend_of_message_id` = the source id, delivery_status
 * queued/sent/failed/unknown). On cross-account targets the server drops
 * the quote (reply anchors are wamids scoped to one account↔customer chat).
 *
 * Errors (FastAPI `{"detail": ...}` bodies): 404 unknown message /
 * conversation; 400 structural (inbound source, unsupported type, target
 * belongs to a different customer); 409 state — source not failed/unknown,
 * "message has already been resent" (the live-resend guard: an earlier
 * resend is queued/sent/delivered/read/unknown), the LID send guard
 * (`lidSendBlockedDetail`), account "... is not connected (status: ...)",
 * "media content is no longer available for resend", and — the hub asks it
 * LAST, once nothing else stands in the way — an `unknown` source without
 * the confirmation ({@link RESEND_CONFIRM_REQUIRED_DETAIL}).
 */
export async function resendMessage(
  messageId: string,
  targetConversationId?: string | null,
  options: ResendMessageOptions = {},
): Promise<Message> {
  const body: { target_conversation_id?: string; confirm_duplicate_risk?: boolean } =
    {};
  if (targetConversationId != null) {
    body.target_conversation_id = targetConversationId;
  }
  if (options.confirmDuplicateRisk === true) {
    body.confirm_duplicate_risk = true;
  }
  const response = await apiClient.post(`/messages/${messageId}/resend`, body);
  return messageOutSchema.parse(response.data);
}

/**
 * `POST /api/v1/messages/{id}/reaction` — set / replace / remove the calling
 * agent's emoji reaction on one message.
 *
 * The backend delivers the reaction to WhatsApp FIRST (gateway react RPC)
 * and persists only on success — a 503 means nothing changed server-side, so
 * the caller (`useReactToMessage`) rolls its optimistic patch back. Returns
 * the target message with its now-current `reactions`.
 */
export async function reactToMessage(
  params: ReactToMessageParams,
): Promise<Message> {
  const { messageId, emoji } = params;
  const response = await apiClient.post(`/messages/${messageId}/reaction`, {
    emoji,
  });
  return messageOutSchema.parse(response.data);
}

/**
 * The Hub's LID send-guard refusal (`hub/app/api/lid_guard.py`), if `error`
 * is one: a 409 from send / reaction / resend when the customer is known
 * only by WhatsApp LID (the gateway would misroute a console reply to a
 * nonexistent number). Its `detail` is a ready-to-show Chinese instruction
 * (reply from the paired phone), so the hooks toast it verbatim. Keyed on
 * the `(LID)` marker in that sentence — the same detail-substring idiom the
 * hooks use for the account-not-connected 409. `null` for any other error.
 */
export function lidSendBlockedDetail(error: unknown): string | null {
  if (!isAxiosError<{ detail?: unknown }>(error) || error.response?.status !== 409) {
    return null;
  }
  const detail = error.response.data?.detail;
  return typeof detail === 'string' && detail.includes('(LID)') ? detail : null;
}
